import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";

const APPROVAL_FACTS_ENDPOINT = "/api/agent/approval-facts";

export interface ApprovalFacts {
  projectName: string | null;
  campaignName?: string | null;
  alertTitle?: string | null;
  /** Fields the server could not verify. The card warns rather than guessing. */
  unverified: string[];
}

/**
 * Resolve an approval's ids into server-verified labels.
 *
 * The card must never describe an action using text the model wrote: a prompt
 * injection reaching the model through a tool result could make the description
 * and the ids disagree, and the user would be approving the description. So the
 * card sends the ids back to the server and renders only what the server
 * confirms against the database and the platform APIs.
 */
export function useApprovalFacts(
  toolName: string,
  input: Record<string, unknown>,
  projectId?: string,
) {
  const { getIdToken } = useAuth();
  const [facts, setFacts] = useState<ApprovalFacts | null>(null);
  const [loading, setLoading] = useState(true);

  // Only the identifying fields matter, and they are stable for a given card —
  // serializing them keeps the effect from re-firing on unrelated re-renders.
  const key = JSON.stringify({
    toolName,
    projectId,
    p: input.projectId,
    c: input.campaignId,
    a: input.alertId,
    pl: input.platform,
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const token = await getIdToken();
        if (!token) return;

        const response = await fetch(APPROVAL_FACTS_ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ tool: toolName, input, projectId }),
        });
        if (!response.ok) return;

        const data = (await response.json()) as { facts?: ApprovalFacts };
        if (!cancelled && data.facts) setFacts(data.facts);
      } catch {
        // Leave facts null — the card falls back to showing the action as
        // unverified, which is the safe direction.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, getIdToken]);

  return { facts, loading };
}
